// @tennis-matches/services/trainerstunden/parent-portal-statements
// Eltern sehen nur freigegebene (versendete) Rechnungen — keine Entwürfe.

import { resolveBillingStatementDocumentMonth } from "./billing-statement-document-month.js";

/** @readonly */
export const PARENT_PORTAL_VISIBLE_STATEMENT_STATUSES = Object.freeze([
  "sent",
  "partially_paid",
  "paid",
]);

function __asStr(x) {
  return String(x ?? "").trim();
}

function __round2(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

/**
 * Bereits beglichener Betrag einer sichtbaren Rechnung (paid_eur oder total − open).
 * @param {object|null|undefined} statement
 */
export function statementSettledPaidEur(statement) {
  const st = statement && typeof statement === "object" ? statement : {};
  const paidField = Number(st.paid_eur ?? st.paidEur) || 0;
  if (paidField > 0.005) return __round2(paidField);
  const total = Number(st.total_eur ?? st.totalEur) || 0;
  const open = Number(st.open_eur ?? st.openEur) || 0;
  return __round2(Math.max(0, total - open));
}

/**
 * Summe bereits gezahlter Beträge aus versendeten Rechnungen im Eltern-Portal.
 * @param {object[]|null|undefined} statements
 */
export function deriveParentPortalPaidEurFromStatements(statements) {
  let total = 0;
  for (const st of filterParentPortalVisibleStatements(statements)) {
    total += statementSettledPaidEur(st);
  }
  return __round2(total);
}

function __statementPeriodYmd(statement) {
  const st = statement && typeof statement === "object" ? statement : {};
  const displayMonth = resolveParentPortalStatementDisplayMonth(st);
  if (/^\d{4}-\d{2}$/.test(displayMonth)) {
    return `${displayMonth}-01`;
  }
  const sent = __asStr(st.sent_at ?? st.sentAt).slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(sent)) return sent;
  const paid = __asStr(st.paid_at ?? st.paidAt).slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(paid)) return paid;
  const periodMonth = __asStr(st.period_month ?? st.periodMonth);
  if (/^\d{4}-\d{2}$/.test(periodMonth)) {
    return `${periodMonth}-01`;
  }
  return "";
}

function __isYmdInRange(ymd, fromYmd, toYmd) {
  const d = __asStr(ymd).slice(0, 10);
  const from = __asStr(fromYmd);
  const to = __asStr(toYmd);
  if (!d || !from || !to) return false;
  return d >= from && d <= to;
}

/**
 * Summe beglichener Beträge im Zeitraum (Kalenderjahr/Saison).
 * @param {object[]|null|undefined} statements
 * @param {string} fromYmd
 * @param {string} toYmd
 */
export function deriveParentPortalPaidEurInRange(statements, fromYmd, toYmd) {
  let total = 0;
  for (const st of filterParentPortalVisibleStatements(statements)) {
    const ymd = __statementPeriodYmd(st);
    if (!__isYmdInRange(ymd, fromYmd, toYmd)) continue;
    total += statementSettledPaidEur(st);
  }
  return __round2(total);
}

/**
 * Zahlungshistorie: Buchungen + beglichene Rechnungen ohne separaten Payment-Datensatz.
 * @param {object[]|null|undefined} payments
 * @param {object[]|null|undefined} statements
 */
export function deriveParentPortalPaymentHistoryRows(payments, statements) {
  const rows = [];
  const coveredStatementIds = new Set();

  for (const p of Array.isArray(payments) ? payments : []) {
    const id = __asStr(p?.id);
    const sid = __asStr(p?.statement_id ?? p?.statementId);
    if (sid) coveredStatementIds.add(sid);
    const date = __asStr(p?.date);
    const amount = Number(p?.amount_eur ?? p?.amountEur) || 0;
    if (!date && amount < 0.005) continue;
    rows.push({
      ...p,
      id: id || `pay:${date}:${amount}`,
      date: date || "—",
      amount_eur: amount,
      method: __asStr(p?.method),
      statement_id: sid,
      childPersonId: __asStr(p?.childPersonId ?? p?.person_id ?? p?.personId),
      source: "payment",
    });
  }

  for (const st of filterParentPortalVisibleStatements(statements)) {
    const sid = __asStr(st?.id);
    if (!sid || coveredStatementIds.has(sid)) continue;
    const amount = statementSettledPaidEur(st);
    if (amount < 0.005) continue;
    const paidAt = __asStr(st.paid_at ?? st.paidAt).slice(0, 10);
    const periodYmd = __statementPeriodYmd(st);
    rows.push({
      id: `stmt-paid:${sid}`,
      date: paidAt || periodYmd || "—",
      amount_eur: amount,
      method: __asStr(st.payment_method ?? st.paymentMethod) || "bank_transfer",
      statement_id: sid,
      childPersonId: __asStr(st.childPersonId ?? st.person_id ?? st.personId),
      payment_reference: __asStr(st.payment_reference ?? st.paymentReference),
      source: "statement",
    });
  }

  return rows.sort((a, b) => __asStr(b.date).localeCompare(__asStr(a.date)));
}

/**
 * @param {object|null|undefined} statement
 * @returns {boolean}
 */
export function isParentPortalVisibleStatement(statement) {
  const st = statement && typeof statement === "object" ? statement : {};
  const status = __asStr(st.status);
  if (!PARENT_PORTAL_VISIBLE_STATEMENT_STATUSES.includes(status)) return false;
  return Boolean(__asStr(st.sent_at ?? st.sentAt));
}

/**
 * @param {object|null|undefined} statement
 * @returns {boolean}
 */
export function isParentPortalOpenStatement(statement) {
  if (!isParentPortalVisibleStatement(statement)) return false;
  const open = Number(statement?.open_eur);
  return Number.isFinite(open) && open > 0.005;
}

/**
 * Anzeige-Monat im Eltern-Portal: Rechnungsmonat (`sent_at` / Rechnungsdatum), nicht `period_month` (Leistungszeitraum).
 *
 * @param {object|null|undefined} statement
 * @returns {string} `YYYY-MM` oder leer
 */
export function resolveParentPortalStatementDisplayMonth(statement) {
  const st = statement && typeof statement === "object" ? statement : {};
  return resolveBillingStatementDocumentMonth(st);
}

/**
 * @param {object[]|null|undefined} statements
 * @returns {object[]}
 */
export function filterParentPortalVisibleStatements(statements) {
  return (Array.isArray(statements) ? statements : []).filter(isParentPortalVisibleStatement);
}

/**
 * @param {object[]|null|undefined} statements
 * @returns {object|null}
 */
export function pickLatestParentPortalStatement(statements) {
  const list = filterParentPortalVisibleStatements(statements);
  if (!list.length) return null;
  return [...list].sort((a, b) => {
    const sa = __asStr(a?.sent_at) || __asStr(a?.period_month);
    const sb = __asStr(b?.sent_at) || __asStr(b?.period_month);
    return sb.localeCompare(sa);
  })[0];
}

/**
 * @param {object[]|null|undefined} statements
 * @returns {{ dueEur: number, openEur: number, openStatementsCount: number, latestStatement: object|null }}
 */
export function deriveParentPortalStatementSummary(statements) {
  const visible = filterParentPortalVisibleStatements(statements);
  let openEur = 0;
  let openStatementsCount = 0;
  for (const st of visible) {
    const open = Number(st?.open_eur);
    if (Number.isFinite(open) && open > 0.005) {
      openEur += open;
      openStatementsCount += 1;
    }
  }
  return {
    dueEur: openEur,
    openEur,
    openStatementsCount,
    latestStatement: pickLatestParentPortalStatement(visible),
  };
}

/**
 * Anstehende Termine chronologisch (Datum, dann Uhrzeit).
 * @param {object[]|null|undefined} sessions
 */
export function sortParentPortalUpcomingSessions(sessions) {
  const list = Array.isArray(sessions) ? sessions : [];
  return [...list].sort((a, b) => {
    const ad = __asStr(a?.date).slice(0, 10);
    const bd = __asStr(b?.date).slice(0, 10);
    if (ad !== bd) return ad.localeCompare(bd);
    return __asStr(a?.start_time).localeCompare(__asStr(b?.start_time));
  });
}

/**
 * @param {object|null|undefined} rawPayload
 * @returns {object}
 */
export function normalizeParentPortalPayload(rawPayload) {
  const src = rawPayload && typeof rawPayload === "object" ? rawPayload : {};
  const statements = filterParentPortalVisibleStatements(src.statements);
  const stmtSummary = deriveParentPortalStatementSummary(statements);
  const prevSummary = src.summary && typeof src.summary === "object" ? src.summary : {};
  const rpcPaid = Number(prevSummary.paidEur ?? prevSummary.paid_eur);
  const paidFromStatements = deriveParentPortalPaidEurFromStatements(statements);
  const latestForPaid =
    stmtSummary.latestStatement ?? prevSummary.latestStatement ?? null;
  const paidFromLatest = statementSettledPaidEur(latestForPaid);
  const paidEur = __round2(
    Math.max(Number.isFinite(rpcPaid) ? rpcPaid : 0, paidFromStatements, paidFromLatest)
  );
  const preparedEur = Number(prevSummary.preparedEur);
  const accruedFromRpc = Number(prevSummary.accruedEur);
  const accruedEur = Number.isFinite(accruedFromRpc) ? accruedFromRpc : 0;
  const viewerProfileRaw = src.viewerProfile ?? src.viewer_profile ?? null;
  const viewerProfile =
    viewerProfileRaw && typeof viewerProfileRaw === "object" ? viewerProfileRaw : null;
  const viewerPersonId = __asStr(src.viewerPersonId ?? src.viewer_person_id);
  return {
    ...src,
    viewerProfile,
    viewerPersonId,
    upcomingSessions: sortParentPortalUpcomingSessions(src.upcomingSessions),
    statements,
    summary: {
      ...prevSummary,
      dueEur: stmtSummary.openEur,
      openEur: stmtSummary.openEur,
      openStatementsCount: stmtSummary.openStatementsCount,
      latestStatement: stmtSummary.latestStatement ?? prevSummary.latestStatement ?? null,
      paidEur,
      preparedEur: Number.isFinite(preparedEur) ? preparedEur : 0,
      accruedEur,
    },
  };
}
